import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, XIcon, ClockIcon, DownloadIcon, MailIcon } from './icons/OrganicIcons';
import Modal from './Modal';

const GraciasPage = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/.netlify/functions/getConfig');
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
          // Disparar verificación de pago en background si hay info local
          try {
            const ls = JSON.parse(localStorage.getItem('guia_lastPay') || 'null');
            if (ls && ls.email) {
              await fetch('/.netlify/functions/verifyPayment-background', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: ls.email, expectedTotal: ls.expectedTotal, edition: data.edition })
              });
            }
          } catch (_) { /* noop */ }
        } else {
          throw new Error('Failed to fetch config');
        }
      } catch (err) {
        console.error('Error fetching config:', err);
        setError('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const addToGoogleCalendar = () => {
    if (!config) return;
    
    const startDate = new Date(config.startIso);
    const endDate = new Date(config.endIso);
    
    // Formato para Google Calendar: YYYYMMDDTHHMMSSZ
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const eventUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('INICIA - Guía AI')}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(`Evento INICIA de Guía AI\n\nAprende a usar IA para potenciar tu trabajo.\n\nLink de Meet: ${config.meetLink}`)}&location=${encodeURIComponent(config.meetLink)}`;
    
    window.open(eventUrl, '_blank');
  };

  const addToOutlook = () => {
    if (!config) return;
    
    const startDate = new Date(config.startIso);
    const endDate = new Date(config.endIso);
    
    // Formato para Outlook: YYYY-MM-DDTHH:MM:SS
    const formatDate = (date) => {
      return date.toISOString().split('.')[0];
    };
    
    const eventUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent('INICIA - Guía AI')}&startdt=${formatDate(startDate)}&enddt=${formatDate(endDate)}&body=${encodeURIComponent(`Evento INICIA de Guía AI\n\nAprende a usar IA para potenciar tu trabajo.\n\nLink de Meet: ${config.meetLink}`)}&location=${encodeURIComponent(config.meetLink)}`;
    
    window.open(eventUrl, '_blank');
  };

  const addToAppleCalendar = () => {
    if (!config) return;
    
    const startDate = new Date(config.startIso);
    const endDate = new Date(config.endIso);
    
    // Formato para Apple Calendar: YYYYMMDDTHHMMSSZ
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const eventUrl = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0AURL:${encodeURIComponent(config.meetLink)}%0ADTSTART:${formatDate(startDate)}%0ADTEND:${formatDate(endDate)}%0ASUMMARY:INICIA - Guía AI%0ADESCRIPTION:Evento INICIA de Guía AI. Aprende a usar IA para potenciar tu trabajo.%0ALOCATION:${encodeURIComponent(config.meetLink)}%0AEND:VEVENT%0AEND:VCALENDAR`;
    
    const link = document.createElement('a');
    link.href = eventUrl;
    link.download = 'INICIA.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openMeet = () => {
    if (config?.meetLink) {
      window.open(config.meetLink, '_blank');
    }
  };

  const openEmail = () => {
    const subject = encodeURIComponent('Confirmación INICIA - GuÍA AI');
    const body = encodeURIComponent(`Hola,\n\nHe confirmado mi participación en INICIA.\n\nSaludos.`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const copyMeetLink = async () => {
    if (config?.meetLink) {
      try {
        await navigator.clipboard.writeText(config.meetLink);
        // Aquí podrías mostrar un toast de confirmación
        alert('Link de Meet copiado al portapapeles');
      } catch (err) {
        console.error('Error copying to clipboard:', err);
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = config.meetLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Link de Meet copiado al portapapeles');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-neutral-700 mb-2">Error</h2>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: CheckIcon,
          iconBg: 'from-green-400 to-green-500',
          title: '¡Gracias!',
          subtitle: 'Tu pago fue recibido',
          description: 'En breve vas a recibir un mail con la fecha y hora de inicio, y el enlace para ingresar a la clase.',
          actions: [
            {
              label: 'Google Calendar',
              icon: DownloadIcon,
              onClick: addToGoogleCalendar,
              variant: 'primary'
            },
            {
              label: 'Outlook',
              icon: DownloadIcon,
              onClick: addToOutlook,
              variant: 'secondary'
            },
            {
              label: 'Apple Calendar',
              icon: DownloadIcon,
              onClick: addToAppleCalendar,
              variant: 'secondary'
            }
          ]
        };

      case 'failure':
        return {
          icon: XIcon,
          iconBg: 'from-red-400 to-red-500',
          title: 'Pago rechazado',
          subtitle: 'Hubo un problema con tu pago',
          description: 'No te preocupes, puedes intentar nuevamente. Si el problema persiste, contáctanos.',
          actions: [
            {
              label: 'Intentar nuevamente',
              icon: DownloadIcon,
              onClick: () => window.location.href = '/inicia',
              variant: 'primary'
            }
          ]
        };

      case 'pending':
        return {
          icon: ClockIcon,
          iconBg: 'from-yellow-400 to-yellow-500',
          title: 'Pago pendiente',
          subtitle: 'Tu pago está siendo procesado',
          description: 'Te notificaremos por email cuando se confirme tu pago. Esto puede tomar unos minutos.',
          actions: [
            {
              label: 'Volver al inicio',
              icon: DownloadIcon,
              onClick: () => window.location.href = '/',
              variant: 'secondary'
            }
          ]
        };

      default:
        return {
          icon: CheckIcon,
          iconBg: 'from-blue-400 to-blue-500',
          title: '¡Gracias por tu interés!',
          subtitle: 'Tu inscripción ha sido recibida',
          description: 'Cuando tu pago se confirme, recibirás un mail con la fecha y hora de inicio del programa.',
          actions: [
            {
              label: 'Volver al inicio',
              icon: DownloadIcon,
              onClick: () => window.location.href = '/',
              variant: 'primary'
            }
          ]
        };
    }
  };

  const content = renderContent();
  const IconComponent = content.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="relative overflow-hidden rounded-[2rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl p-12 text-center">
          <div className="space-y-8">
            {/* Icon */}
            <div className="flex justify-center">
              <div className={`w-24 h-24 bg-gradient-to-br ${content.iconBg} rounded-3xl flex items-center justify-center shadow-2xl`}>
                <IconComponent className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h1 className="text-4xl font-light text-neutral-700 leading-tight" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                {content.title}
              </h1>
              <h2 className="text-xl text-neutral-600 leading-relaxed" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                {content.subtitle}
              </h2>
              <p className="text-lg text-neutral-600 leading-relaxed max-w-md mx-auto" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                {content.description}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {content.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 ${
                    action.variant === 'primary'
                      ? 'bg-gradient-to-r from-green-400 to-blue-400 text-white shadow-lg hover:shadow-xl hover:scale-105'
                      : action.variant === 'secondary'
                      ? 'bg-white border-2 border-green-400 text-green-600 hover:bg-green-50'
                      : 'bg-white border-2 border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                  }`}
                  style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
                >
                  <action.icon className="w-5 h-5" />
                  <span>{action.label}</span>
                </button>
              ))}
              {/* Botón para re-disparar verificación en background */}
              <button
                onClick={async () => {
                  try {
                    const ls = JSON.parse(localStorage.getItem('guia_lastPay') || 'null');
                    if (ls && ls.email) {
                      await fetch('/.netlify/functions/verifyPayment-background', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: ls.email, expectedTotal: ls.expectedTotal, edition: config?.edition })
                      });
                      setConfirmOpen(true);
                    } else {
                      setConfirmOpen(true);
                    }
                  } catch (_) { setConfirmOpen(true); }
                }}
                className="px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-green-400 to-blue-400 text-white shadow-lg hover:shadow-xl hover:scale-105"
              >
                Ya hice mi pago
              </button>
            </div>

            {/* Additional Info */}
            {status === 'success' && config && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
                <h3 className="text-lg font-medium text-neutral-700 mb-3" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                  Detalles del evento
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-neutral-600 mb-4">
                  <div>
                    <strong>Fecha:</strong> {new Date(config.startIso).toLocaleDateString('es-AR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div>
                    <strong>Hora:</strong> {new Date(config.startIso).toLocaleTimeString('es-AR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })} - {new Date(config.endIso).toLocaleTimeString('es-AR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                  <div>
                    <strong>Zona horaria:</strong> {config.tz || 'America/Argentina/Buenos_Aires'}
                  </div>
                  <div>
                    <strong>Plataforma:</strong> Google Meet
                  </div>
                </div>
                
                {/* Additional Actions */}
                <div className="border-t border-green-200 pt-4">
                  <h4 className="text-md font-medium text-neutral-700 mb-3" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                    Acciones rápidas
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <button
                      onClick={openMeet}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <MailIcon className="w-4 h-4" />
                      <span>Abrir Meet</span>
                    </button>
                    <button
                      onClick={copyMeetLink}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <DownloadIcon className="w-4 h-4" />
                      <span>Copiar Link</span>
                    </button>
                    <button
                      onClick={openEmail}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <MailIcon className="w-4 h-4" />
                      <span>Abrir correo</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal confirmación de verificación */}
            {confirmOpen && (
              <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <div className="text-center p-6">
                  <h3 className="text-xl text-neutral-800 mb-3">Estamos verificando tu pago</h3>
                  <p className="text-neutral-600 mb-6">Te avisaremos por correo cuando esté confirmado.</p>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    className="px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-green-400 to-blue-400 text-white shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    Entendido
                  </button>
                </div>
              </Modal>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraciasPage;
